import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where
} from 'firebase/firestore';
import { db } from './firebase';

const PAYMENTS_COLLECTION = 'payments';
const USERS_COLLECTION = 'users';

// Real-time cache of users to resolve userId -> { fullName, email }
const usersCache = new Map();
let usersSubscribed = false;

const initUsersListener = () => {
  if (usersSubscribed) return;
  usersSubscribed = true;
  onSnapshot(collection(db, USERS_COLLECTION), (snapshot) => {
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      usersCache.set(doc.id, {
        fullName: data.fullName || '',
        email: data.email || '',
      });
    });
  }, (err) => {
    console.warn('Could not listen to users for payment enrichment:', err);
  });
};

/**
 * Format and enrich payment document
 */
const enrichPaymentDoc = (doc) => {
  const data = doc.data() || {};
  const userCached = data.userId ? usersCache.get(data.userId) : null;

  const studentEmail = data.email || data.customerEmail || data.paystackResponse?.customer?.email || userCached?.email || '';
  const studentName = data.student || data.studentName || data.userName || userCached?.fullName || (studentEmail ? studentEmail.split('@')[0] : 'Unknown Student');

  const rawAmount = data.amount || data.amountValue || 0;
  let formattedAmount = '₦0';
  if (typeof rawAmount === 'string' && rawAmount.startsWith('₦')) {
    formattedAmount = rawAmount;
  } else {
    const num = typeof rawAmount === 'number' ? rawAmount : parseFloat(String(rawAmount).replace(/[^0-9.-]+/g, ''));
    formattedAmount = isNaN(num) ? '₦0' : `₦${num.toLocaleString()}`;
  }

  const dateValue = data.paidAt || data.createdAt || data.date || null;

  return {
    id: doc.id,
    ...data,
    email: studentEmail,
    student: studentName,
    amount: formattedAmount,
    date: dateValue,
    transactionId: data.transactionId || data.paymentReference || data.reference || doc.id,
    paymentGateway: data.paymentGateway || 'Paystack',
    plan: data.plan || (data.tier ? `${data.tier.charAt(0).toUpperCase() + data.tier.slice(1)} Plan` : 'Subscription'),
  };
};

/**
 * Subscribe to all payments with real-time updates
 */
export const subscribeToPayments = (callback) => {
  initUsersListener();

  const q = query(
    collection(db, PAYMENTS_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const payments = snapshot.docs.map(enrichPaymentDoc);
    callback(payments);
  });
};

/**
 * Subscribe to payments with filter
 */
export const subscribeToPaymentsByStatus = (status, callback) => {
  initUsersListener();

  const q = query(
    collection(db, PAYMENTS_COLLECTION),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const payments = snapshot.docs.map(enrichPaymentDoc);
    callback(payments);
  });
};

/**
 * Add a new payment
 */
export const addPayment = async (paymentData) => {
  try {
    const docRef = await addDoc(collection(db, PAYMENTS_COLLECTION), {
      ...paymentData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding payment:', error);
    throw error;
  }
};

/**
 * Update a payment
 */
export const updatePayment = async (paymentId, updates) => {
  try {
    const paymentRef = doc(db, PAYMENTS_COLLECTION, paymentId);
    await updateDoc(paymentRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating payment:', error);
    throw error;
  }
};

/**
 * Delete a payment
 */
export const deletePayment = async (paymentId) => {
  try {
    const paymentRef = doc(db, PAYMENTS_COLLECTION, paymentId);
    await deleteDoc(paymentRef);
  } catch (error) {
    console.error('Error deleting payment:', error);
    throw error;
  }
};

/**
 * Calculate payment statistics
 */
export const calculatePaymentStats = (payments) => {
  const total = payments.length;
  const success = payments.filter(p => p.status === 'success').length;
  const pending = payments.filter(p => p.status === 'pending').length;
  const failed = payments.filter(p => p.status === 'failed').length;

  // Calculate total revenue (only successful payments)
  const totalRevenue = payments
    .filter(p => p.status === 'success')
    .reduce((sum, p) => {
      const amount = parseFloat(p.amount?.replace(/[^0-9.-]+/g, '') || 0);
      return sum + amount;
    }, 0);

  // Calculate today's revenue
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayRevenue = payments
    .filter(p => {
      if (p.status !== 'success') return false;
      const paymentDate = p.createdAt?.toDate?.() || new Date(p.createdAt);
      return paymentDate >= today;
    })
    .reduce((sum, p) => {
      const amount = parseFloat(p.amount?.replace(/[^0-9.-]+/g, '') || 0);
      return sum + amount;
    }, 0);

  // Calculate this month's revenue
  const thisMonth = new Date();
  thisMonth.setDate(1);
  thisMonth.setHours(0, 0, 0, 0);
  const monthlyRevenue = payments
    .filter(p => {
      if (p.status !== 'success') return false;
      const paymentDate = p.createdAt?.toDate?.() || new Date(p.createdAt);
      return paymentDate >= thisMonth;
    })
    .reduce((sum, p) => {
      const amount = parseFloat(p.amount?.replace(/[^0-9.-]+/g, '') || 0);
      return sum + amount;
    }, 0);

  return {
    total,
    success,
    pending,
    failed,
    totalRevenue,
    todayRevenue,
    monthlyRevenue,
  };
};

export default {
  subscribeToPayments,
  subscribeToPaymentsByStatus,
  addPayment,
  updatePayment,
  deletePayment,
  calculatePaymentStats,
};
